import React, {useCallback, useEffect, useState} from 'react'
import Header from 'lib/ui/layout/Header'
import {Title} from 'lib/ui/typography'
import Page from 'organization/Event/TemplateEditor/Page'
import {Alert} from '@material-ui/lab'
import styled from 'styled-components'
import WidgetsList from 'organization/Event/WidgetConfig/WidgetsList'
import {RegenerateTokenButton} from 'organization/Event/WidgetConfig/RegenerateTokenButton'
import {spacing} from 'lib/ui/theme'
import SaveButton from 'organization/Event/WidgetConfig/SaveButton'
import TagsConfig from 'organization/Event/WidgetConfig/TagsConfig'
import GroupsConfig from 'organization/Event/WidgetConfig/GroupsConfig'
import AllowedOriginsConfig from 'organization/Event/WidgetConfig/AllowedOriginsConfig'
import SourcesConfig from 'organization/Event/WidgetConfig/SourcesConfig'
import {useWidget} from 'lib/event-api/widgets/use-widget'
import {Widget} from 'Event/Widget/template'
import {ProductionBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'

export default function WidgetConfig() {
  const {data: serverWidget} = useWidget()
  const [widget, setWidget] = useState<Widget | null>(null)

  useEffect(() => {
    if (
      (serverWidget && !widget) ||
      (serverWidget && serverWidget?.token !== widget?.token)
    ) {
      setWidget(serverWidget)
    }
  }, [serverWidget, widget])

  const updateWidget = useCallback((updatedFields: Partial<Widget>) => {
    setWidget((prev) => (prev ? {...prev, ...updatedFields} : prev))
  }, [])

  return (
    <ProductionBreadcrumbs page="Widgets">
      <Page
        rightToolbarItems={
          <ButtonsHolder>
            <RegenerateTokenButton />
            <SaveButton widget={widget} />
          </ButtonsHolder>
        }
      >
        <Container>
          <StyledHeader>
            <Title>Widgets</Title>
          </StyledHeader>
          <Alert severity="warning" style={{marginBottom: '16px'}}>
            <>
              <b>Widgets are not secure</b> <br />
              Anybody could potentially use them to create attendees for your
              event. <br />
              This is only recommended for advanced users and typically for Free
              events. <br />
              <br />
              <b>Hint:</b> If your widget isn't rendering, please check your
              browser's console.
            </>
          </Alert>

          <>
            <SubTitle>Configuration</SubTitle>
            <SubTitleDescription>
              Configure various settings for widgets
            </SubTitleDescription>
            <h5>Allowed Origins</h5>

            <AllowedOriginsConfig widget={widget} updateWidget={updateWidget} />
            <h5>Add Tags to created users via Widgets</h5>
            <TagsConfig widget={widget} updateWidget={updateWidget} />

            <h5>Add Groups to created users via Widgets</h5>
            <GroupsConfig widget={widget} updateWidget={updateWidget} />

            <SubTitle>Sources</SubTitle>
            <SubTitleDescription>
              Configure where user data should be sourced from
            </SubTitleDescription>

            <SourcesConfig widget={widget} updateWidget={updateWidget} />

            <SubTitle>Code Generator</SubTitle>
            <SubTitleDescription>
              Configure where user data should be sourced from
            </SubTitleDescription>
            <WidgetsList widget={widget} updateWidget={updateWidget} />
          </>
        </Container>
      </Page>
    </ProductionBreadcrumbs>
  )
}

const StyledHeader = styled(Header)`
  margin-top: ${spacing[4]} !important;
  margin-bottom: ${spacing[4]} !important;
`

export const SubTitle = styled.h4`
  margin-top: ${spacing[10]};
`

const SubTitleDescription = styled.p`
  margin-bottom: ${spacing[6]};
  margin-top: -${spacing[2]};
  font-size: 0.9rem;
  color: #6a7181;
`

const Container = styled.div`
  padding: 0 60px 20px;
`

const ButtonsHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
